// Audio clips that come from a link rather than a copy of the file.
//
// Uploading writes the clip into the deck as a base64 data URL, and audio is
// the worst offender for that: the 0.9 MB territorial acknowledgement this was
// built for becomes ~1.2 MB of JSON, carried by every saved version, every
// autosave and every export. A link keeps the clip where it is already hosted —
// a Cloudflare R2 bucket, an LMS folder, any CDN — and the deck stores the
// address instead. Same bargain the picture links make (see imageLink.js).
//
// What arrives here is simpler than a picture link, because there is no
// equivalent of a Pinterest page that publishes a clip in its metadata. An
// audio link is either a file the browser can play or it is nothing, so there
// is no provider lookup — only the one question worth asking:
//
//     **does this address actually play?**
//
// which is answered the way imageLink answers its own question: by doing the
// thing rather than guessing at it. A file extension is a hint, not an answer —
// signed CDN links and LMS handlers routinely end in a token instead of `.mp3`,
// and a `.mp3` that 404s is still not a clip. So the probe hands the address to
// an <audio> element and waits to see whether metadata arrives. That costs a
// metadata-only read (`preload="metadata"`, a few KB, not the whole file), it
// needs no CORS grant from the host, and it comes back with the clip's duration
// — which is worth having anyway.

import { toFetchableUrl } from './assetUrls.js';

const AUDIO_FILE_PATH = /\.(mp3|m4a|mp4|aac|wav|wave|ogg|oga|opus|flac|weba|webm)$/i;

const isHttpsUrl = (value) => typeof value === 'string' && /^https:\/\//i.test(value.trim());

export const isAudioDataUrl = (value) =>
  typeof value === 'string' && /^data:audio\//i.test(value.trim());

export const NOT_A_LINK_MESSAGE =
  'That does not look like an https link. Copy the clip\u2019s address \u2014 the one ending in .mp3, .m4a or .wav \u2014 and paste it again.';

/** How long to wait for a host to answer with the clip's metadata. */
export const PROBE_TIMEOUT_MS = 20000;

const safeUrl = (raw) => {
  try {
    const url = new URL(String(raw).trim());
    return /^https?:$/.test(url.protocol) ? url : null;
  } catch {
    return null;
  }
};

/**
 * The clip's own name, for prefilling an empty title.
 *
 * The path segment is percent-decoded, so
 * `/Audio%20Clips/Territorial%20Acknowledgement.mp3` gives back
 * "Territorial Acknowledgement" rather than the escaped form.
 */
export const audioFileName = (value) => {
  const url = safeUrl(value);
  if (!url) return '';
  const last = url.pathname.split('/').pop() || '';
  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch { /* an undecodable segment is still usable as-is */ }
  return name.replace(AUDIO_FILE_PATH, '');
};

/**
 * What a pasted link is, before any network call.
 *
 * @returns {{url, name, looksLikeFile}|null} null when the input is empty, not
 *          https, or not a URL at all. `looksLikeFile` records whether the path
 *          ends in an audio extension — a hint used only for the wording of a
 *          failure, never to refuse a link outright.
 */
export const describeAudioLink = (raw) => {
  const input = typeof raw === 'string' ? raw.trim() : '';
  if (!input) return null;

  if (isAudioDataUrl(input)) {
    return { url: input, name: 'Audio clip', looksLikeFile: true };
  }

  const url = safeUrl(input);
  if (!url || !isHttpsUrl(url.href)) return null;

  return {
    url: url.href,
    name: audioFileName(url.href) || url.hostname,
    looksLikeFile: AUDIO_FILE_PATH.test(url.pathname)
  };
};

/**
 * Hand the address to an <audio> element and report what happened.
 *
 * `preload="metadata"` asks for the header rather than the file, so this is a
 * few KB even for an hour-long recording. No `crossOrigin` is set: playback
 * never needs a CORS grant, and asking for one here would fail links that play
 * perfectly well. (The waveform *does* need one — see audioWaveform.js — but
 * that is a rendering nicety with its own fallback, not a reason to reject a
 * clip at the moment it is pasted.)
 *
 * @returns {Promise<{ok: boolean, duration: number, error: string}>}
 *          Never throws. `duration` is 0 for a stream whose length the header
 *          does not state, which is a playable clip, not a failure.
 */
export const probeAudioUrl = (url, { timeoutMs = PROBE_TIMEOUT_MS } = {}) => {
  if (typeof Audio === 'undefined') {
    // No media element to ask (server-side render, or a test environment that
    // does not implement one). Nothing has been disproved, so nothing is
    // claimed: the link is accepted without a duration.
    return Promise.resolve({ ok: true, duration: 0, error: '' });
  }

  return new Promise((resolve) => {
    const element = new Audio();
    let settled = false;
    let timer = null;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      element.removeEventListener('loadedmetadata', onLoaded);
      element.removeEventListener('error', onError);
      // Stop the transfer. Without this the element keeps buffering a clip
      // nobody is going to play yet.
      element.removeAttribute('src');
      try { element.load(); } catch { /* already torn down */ }
      resolve(result);
    };

    function onLoaded() {
      const duration = Number(element.duration);
      finish({ ok: true, duration: Number.isFinite(duration) ? duration : 0, error: '' });
    }

    function onError() {
      // MediaError codes say roughly what went wrong; the wording below is what
      // the panel shows, so it names the likely cause rather than a number.
      const code = element.error?.code;
      const reason = code === 4
        ? 'the browser could not play that file \u2014 check the address, or the format'
        : code === 2
          ? 'the download failed \u2014 check the address'
          : 'the host did not serve a playable clip';
      finish({ ok: false, duration: 0, error: reason });
    }

    element.addEventListener('loadedmetadata', onLoaded);
    element.addEventListener('error', onError);
    element.preload = 'metadata';
    element.src = toFetchableUrl(url);

    timer = setTimeout(() => {
      finish({ ok: false, duration: 0, error: 'the host did not answer in time' });
    }, timeoutMs);
  });
};

/**
 * Turn whatever was pasted into a clip address, having checked that it plays.
 *
 * @returns {Promise<{ok: true, url, name, duration}|{ok: false, error}>}
 *          Never throws: a failure is an outcome the panel shows, not a crash.
 */
export const resolveAudioLink = async (raw, { probe = probeAudioUrl } = {}) => {
  const described = describeAudioLink(raw);
  if (!described) return { ok: false, error: NOT_A_LINK_MESSAGE };

  const result = await probe(described.url);
  if (!result?.ok) {
    const detail = result?.error || 'the host did not serve a playable clip';
    const hint = described.looksLikeFile
      ? ''
      : ' That address does not end in an audio file name, so it may be the page the clip sits on rather than the clip itself.';
    return { ok: false, error: `Could not play that link \u2014 ${detail}.${hint}` };
  }

  return {
    ok: true,
    url: described.url,
    name: described.name,
    duration: Number(result.duration) || 0
  };
};
